import type { TiptapEditor } from '../Editor/Editor';
import { useBriefFields } from './useBriefFields';

/**
 * The quiet strip at the foot of a brief listing the fields that are switched
 * off. Switching a field off keeps its content, so it should not simply
 * vanish: the strip is the reminder that it is still there, and `Show` puts it
 * back in the position it held without a trip to the drawer.
 *
 * Renders nothing when every field is on, and never prints.
 */
export default function HiddenFieldsStrip({ editor }: { editor: TiptapEditor | null }) {
  const hidden = useBriefFields(editor).filter((f) => !f.attrs.enabled);

  if (!editor || hidden.length === 0) return null;

  return (
    <div data-print-hide className="mt-10 pt-5 border-t border-dashed border-line">
      <span className="block font-mono text-[10px] uppercase tracking-[0.14em] text-faint">
        Hidden fields ({hidden.length})
      </span>
      <ul className="flex flex-wrap gap-2 mt-3">
        {hidden.map(({ attrs }) => (
          <li
            key={attrs.id}
            className="
              inline-flex items-center gap-2 pl-3 pr-2 py-1.5 rounded-full
              bg-elevated border border-line text-[12px] text-muted
            "
          >
            <span className="max-w-[220px] truncate" title={attrs.title}>
              {attrs.title}
            </span>
            <button
              type="button"
              onClick={() => editor.commands.showBriefField(attrs.id)}
              title={`Put "${attrs.title}" back in the document, content intact.`}
              className="
                font-medium text-accent hover:text-accent-hover
                underline decoration-transparent hover:decoration-current
                underline-offset-2 transition-colors duration-150
              "
            >
              Show
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
